export type CollectionSorting<TSortField extends string = string> = readonly Readonly<{
  id: TSortField
  desc: boolean
}>[]
export type CollectionItemState = "unloaded" | "loading" | "loaded" | "error"

/** Consumer view of a collection: records, identity, paging, ordering, selection and change notification.
 * It does not describe how records are queried, stored or transported. Owners keep lifecycle and mutation commands.
 */
export interface CollectionSource<TItem, TSortField extends string = string> {
  readonly sorting: CollectionSorting<TSortField>
  readonly selectedKeys: readonly string[]
  readonly capabilities: { readonly sorting: readonly TSortField[]; readonly maxSortFields: number }
  readonly pageSize: number
  /** Page-zero error takes precedence; otherwise ready once a page/records have loaded. */
  readonly status: "loading" | "error" | "ready"
  /** Page-zero failure, including failed refresh with stale rows still present. */
  readonly error: unknown
  /** Changes on every observable change. */
  readonly revision: number
  /** Changes when records or their positions change; selection alone does not change it. */
  readonly dataRevision: number
  /** Changes whenever row positions belong to a new result set. */
  readonly resultSetRevision: number
  readonly itemCount: number
  readonly totalCount: number | undefined
  readonly loadedCount: number
  readonly isLoading: boolean
  get(index: number): TItem | undefined
  /** Enumerates cached records, including stale pages awaiting on-demand refresh. */
  getLoadedEntries(): Iterable<readonly [number, TItem]>
  getKey(item: TItem): string
  getItemState(index: number): CollectionItemState
  getItemError(index: number): unknown
  getItemErrorRange(index: number): Readonly<{ start: number; end: number }> | undefined
  setSorting(sorting: CollectionSorting<TSortField>): void
  setSelectedKeys(keys: readonly string[]): void
  ensureRange(start: number, end: number): void
  retryRange(start: number, end: number): Promise<void>
  subscribe(listener: () => void): () => void
  /** Notified only when dataRevision changes. */
  subscribeData(listener: () => void): () => void
}

/** Optional capability of a source whose result set is chosen by a query.
 * The query's shape and validation belong to the source that interprets it.
 */
export interface QueryableCollection<TQuery> {
  readonly query: Readonly<TQuery>
  applyQuery(query: TQuery | ((previous: Readonly<TQuery>) => TQuery)): void
}

/** How far a completable collection has come in holding every record of its current result set. */
export type CollectionCompleteness =
  /** Records load by range. No consumer needs all of them. */
  | { readonly state: "partial" }
  /** Loading the whole result set. `total` is undefined when the backend does not report a count. */
  | { readonly state: "loading"; readonly loaded: number; readonly total: number | undefined }
  /** Every record of the current result set is in memory. */
  | { readonly state: "complete" }
  /** The result set has more than `limit` records; nothing more is loaded for completeness. */
  | { readonly state: "too-large"; readonly total: number | undefined; readonly limit: number }
  /** Loading stopped at a failed request; `retry()` resumes it. */
  | {
      readonly state: "error"
      readonly error: unknown
      readonly loaded: number
      readonly total: number | undefined
      retry(): void
    }

/** Optional capability of a collection that can hold every record of its current result set in memory.
 * `completeness` changes notify `subscribe`; it keeps its identity until it changes.
 */
export interface CompletableCollection<TItem> {
  readonly completeness: CollectionCompleteness
  /**
   * Asks for every record of the current result set, and of later result sets while held. Returns an idempotent
   * release. Demands are counted. Releasing the last one keeps the records; the collection stays complete until its
   * result set changes.
   */
  requireComplete(): () => void
  /** Every record in result order while complete; otherwise undefined. Same array until `dataRevision` changes. */
  getAllItems(): readonly TItem[] | undefined
}

/** Whether a source can load its whole result set. A remote collection can when it is created with `complete`. */
export function isCompletableCollection<TItem, TSortField extends string>(
  source: CollectionSource<TItem, TSortField>
): source is CollectionSource<TItem, TSortField> & CompletableCollection<TItem> {
  const candidate = source as CollectionSource<TItem, TSortField> & Partial<CompletableCollection<TItem>>
  return candidate.completeness !== undefined && typeof candidate.requireComplete === "function"
}
